import axios, { AxiosRequestConfig } from 'axios';
import { Note, NoteBacklinksData, NoteCle, ApercuConceptDto, FaceAFaceDto, ContexteCitationDto } from '../types/api';

/**
 * Provenance web d'une capture (Web Clipper, C9) : la note devient une note
 * source. L'app n'en envoie pas aujourd'hui (c'est l'extension qui le fait),
 * mais le contrat de `POST /api/notes/capture` est typé ici, à côté du client.
 */
export interface CaptureSource {
    url: string;
    title?: string;
    textFragment?: string;
}

/**
 * Provenance « document de périmètre » d'une capture (lecteur mobile, C11 SP3) :
 * le serveur retrouve le fichier, construit `sourceRef` et `locators`, la note
 * devient une note source. Miroir de `CapturePassage` (api/src/utils/passageSource.ts).
 */
export interface CapturePassage {
    fileId: string;
    fileType: 'epub' | 'html' | 'docx' | 'pdf';
    cfi?: string;
    chapter?: number;
    chapterLabel?: string;
    page?: number;
    /** PDF : dernière page d'une sélection qui en chevauche plusieurs (C11, 19/09). */
    pageEnd?: number;
    paragraphEstimate?: number;
    startOffset?: number;
    endOffset?: number;
    /** PDF : plage de lignes du passage sur sa page (C11, 19/09) — `locators.lines` de la note. */
    lineStart?: number;
    lineEnd?: number;
    /**
     * PDF, « Citer » (C11, 19/09) : les lignes du passage avec leur géométrie ;
     * le serveur en fait les ancres `source-line` du contenu, comme le bureau
     * (`api/src/utils/sourceNoteLines.ts`).
     */
    lines?: CapturePassageLine[];
}

/** Une ligne du passage, telle que `useTextSelection` la relève (miroir de `SelectionLineData`). */
export interface CapturePassageLine {
    lineNumber: number;
    text: string;
    page?: number;
    y: number;
    height: number;
    yPercent: number;
}

/**
 * Mot-clé ancré dans une capture (C11 SP6) : le serveur pose `id` sur la
 * première occurrence de `text` dans le texte capturé (miroir de
 * `CaptureAnchor`, api/src/functions/notes-capture.ts).
 */
export interface CaptureEntree {
    id: string;
    /** Passage à marquer ; absent → le mot-clé est posé sur la note seule (C11 SP6 ter, capture rapide). */
    text?: string;
}

/**
 * Marque que le remplacement du passage d'une citation n'a pas pu reposer
 * (06/10/2026, `PUT /api/notes/{noteId}/passage`) : un concept dont le passage
 * ne se retrouve plus dans le nouveau texte, ou une note reliée (`noteReliee`,
 * `id` est alors la note cible et `cle` son titre). Miroir de `MarquePerdue`
 * (api/src/utils/reanchorPassage.ts).
 */
export interface LostPassageMark {
    id: string;
    cle: string;
    text: string;
    noteReliee?: true;
}

/** Corps de `PUT /api/notes/{noteId}/passage` : le texte retenu (citation seulement) et le passage, comme à la capture. */
export interface ReplacePassageBody {
    text?: string;
    passage: CapturePassage;
    ifUnmodifiedSince?: string;
}

/**
 * Configuration pour les requêtes API avec authentification
 * @param accessToken - Token d'accès Azure B2C
 * @returns Configuration axios avec headers d'authentification
 */
const createConfig = (accessToken: string): AxiosRequestConfig => ({
    headers: {
        'Authorization': `Bearer ${accessToken}`,
        'AccessToken': accessToken,
        'Content-Type': 'application/json'
    }
});

/**
 * Service API pour la gestion des notes
 * Toutes les méthodes sont typées avec TypeScript strict
 */
/**
 * Garde de version facultative de `PUT /api/notes/{id}` (C22 SP1, § 2.6) :
 * `ifUnmodifiedSince` est l'`updatedDate` (ISO) de la note telle que le client
 * l'a chargée ; si elle a changé depuis, le serveur répond 409 `NOTE_MODIFIEE`
 * sans rien écrire. Absente : le dernier écrit gagne.
 */
export interface UpdateContentOptions {
    ifUnmodifiedSince?: string;
}

/**
 * Le serveur a refusé l'écriture (409) : la note a changé depuis son
 * chargement par le client (garde `ifUnmodifiedSince`). Rien n'a été écrit.
 */
export class NoteModifieeAilleursError extends Error {
    readonly status = 409;
    constructor(message?: string) {
        super(message ?? 'La note a été modifiée depuis son chargement');
        this.name = 'NoteModifieeAilleursError';
    }
}

export const noteService = {
    /**
     * Récupère toutes les notes de l'utilisateur
     * @param accessToken - Token d'authentification
     * @returns Liste des notes
     */
    getNotes: async (accessToken: string): Promise<Note[]> => {
        try {
            // Demander une limite élevée pour récupérer toutes les notes
            const response = await axios.get('/api/notes?limit=1000', createConfig(accessToken));
            // L'API retourne { success: true, data: notes[] }
            return response.data.data || response.data;
        } catch (error) {
            console.error('Erreur lors de la récupération des notes:', error);
            throw new Error('Impossible de récupérer les notes');
        }
    },

    /**
     * Capture rapide (C4) : crée une note « non triée » depuis du texte brut.
     * Le serveur construit le contenu et choisit GLOBAL si aucun périmètre n'est fourni.
     */
    capture: async (
        text: string,
        perimetreId: string | undefined,
        accessToken: string,
        source?: CaptureSource,
        passage?: CapturePassage,
        entrees?: CaptureEntree[],
        /** `commentary` : le texte est le commentaire de l'utilisateur sur le passage (C11, 19/09). */
        noteType?: 'commentary',
    ): Promise<Note> => {
        const response = await axios.post(
            '/api/notes/capture',
            {
                text, perimetreId,
                ...(source ? { source } : {}), ...(passage ? { passage } : {}),
                ...(entrees && entrees.length > 0 ? { entrees } : {}),
                ...(noteType ? { noteType } : {}),
            },
            createConfig(accessToken)
        );
        return response.data.data || response.data;
    },

    /**
     * Met à jour le contenu d'une note par son JSON seul (C11 SP6, mots-clés
     * ancrés depuis le mobile) : `PUT { content: { JSON }, cles }` — jamais
     * `content.text`, que le serveur reprojette (invariant CONTENT_TEXT_PROJECTION).
     * Les erreurs axios remontent telles quelles (le thunk lit le statut).
     */
    updateContent: async (
        noteId: string,
        JSON: unknown,
        cles: NoteCle[],
        accessToken: string,
        options?: UpdateContentOptions
    ): Promise<Note> => {
        const body: { content: { JSON: unknown }; cles: NoteCle[]; ifUnmodifiedSince?: string } = { content: { JSON }, cles };
        if (options?.ifUnmodifiedSince) body.ifUnmodifiedSince = options.ifUnmodifiedSince;
        const response = await axios.put(
            `/api/notes/${encodeURIComponent(noteId)}`,
            body,
            createConfig(accessToken)
        );
        return response.data.data || response.data;
    },

    /**
     * Renomme une note (shell mobile, 11/10/2026) : `PUT { title }` seul — le
     * serveur ne touche ni au contenu ni aux mots-clés. Même garde de version
     * facultative que `updateContent`. Les erreurs axios remontent telles quelles.
     */
    updateTitle: async (
        noteId: string,
        title: string,
        accessToken: string,
        options?: UpdateContentOptions
    ): Promise<Note> => {
        const body: { title: string; ifUnmodifiedSince?: string } = { title };
        if (options?.ifUnmodifiedSince) body.ifUnmodifiedSince = options.ifUnmodifiedSince;
        const response = await axios.put(
            `/api/notes/${encodeURIComponent(noteId)}`,
            body,
            createConfig(accessToken)
        );
        return response.data.data || response.data;
    },

    /**
     * Range une note dans ses dossiers (shell mobile, 19/10/2026) : `PUT { perimetres }`
     * seul — le premier est le dossier principal. Même garde de version facultative
     * que `updateContent`. Les erreurs axios remontent telles quelles.
     */
    updatePerimetres: async (
        noteId: string,
        perimetres: Note['perimetres'],
        accessToken: string,
        options?: UpdateContentOptions
    ): Promise<Note> => {
        const body: { perimetres: Note['perimetres']; ifUnmodifiedSince?: string } = { perimetres };
        if (options?.ifUnmodifiedSince) body.ifUnmodifiedSince = options.ifUnmodifiedSince;
        const response = await axios.put(
            `/api/notes/${encodeURIComponent(noteId)}`,
            body,
            createConfig(accessToken)
        );
        return response.data.data || response.data;
    },

    /**
     * Modifier le passage d'une citation ou d'un commentaire (06/10/2026) :
     * `PUT /api/notes/{noteId}/passage` — le serveur reconstruit contenu (citation),
     * `sourceRef`, `locators` et titre par défaut, et rapporte les marques perdues.
     * Les erreurs axios remontent telles quelles (le thunk lit le statut).
     */
    replacePassage: async (
        noteId: string,
        body: ReplacePassageBody,
        accessToken: string,
    ): Promise<{ note: Note; lost: LostPassageMark[] }> => {
        const response = await axios.put(
            `/api/notes/${encodeURIComponent(noteId)}/passage`,
            body,
            createConfig(accessToken)
        );
        return { note: response.data.data, lost: Array.isArray(response.data.lost) ? response.data.lost : [] };
    },

    /**
     * Backlinks (C6) : notes qui référencent `noteId` par [[Titre]] et notes qui
     * partagent ses entrées, filtrées par ce que l'utilisateur peut lire.
     * Les erreurs remontent à l'appelant (le thunk les traduit en état `failed`).
     */
    /**
     * Aperçu d'un concept derrière une marque d'une note lisible (C23 SP1) :
     * 404 quand rien ne l'ouvre (note illisible, marque non validée, concept d'un tiers).
     */
    getApercuConcept: async (noteId: string, entreeId: string, accessToken: string): Promise<ApercuConceptDto> => {
        const response = await axios.get(
            `/api/notes/${encodeURIComponent(noteId)}/concepts/${encodeURIComponent(entreeId)}`,
            createConfig(accessToken)
        );
        return response.data.data;
    },

    /**
     * « Face à face » (C23 SP2) : le côté de l'auteur de la note — son concept, ses
     * voisins, ses passages dans ses notes que je peux lire. Même porte que l'aperçu :
     * 404 quand rien ne l'ouvre.
     */
    getFaceAFace: async (noteId: string, entreeId: string, accessToken: string): Promise<FaceAFaceDto> => {
        const response = await axios.get(
            `/api/notes/${encodeURIComponent(noteId)}/concepts/${encodeURIComponent(entreeId)}/face-a-face`,
            createConfig(accessToken)
        );
        return response.data.data;
    },

    /**
     * Le contexte d'une citation (C23 SP5) : la phrase d'avant et d'après, le paragraphe,
     * lus dans le document du propriétaire de la note. 404 quand la note n'est pas lisible ;
     * 200 `indisponible` avec sa raison quand le contexte ne peut pas venir.
     */
    getContexteCitation: async (noteId: string, accessToken: string): Promise<ContexteCitationDto> => {
        const response = await axios.get(
            `/api/notes/${encodeURIComponent(noteId)}/contexte`,
            createConfig(accessToken)
        );
        return response.data.data;
    },

    getBacklinks: async (noteId: string, accessToken: string): Promise<NoteBacklinksData> => {
        const response = await axios.get(
            `/api/notes/${encodeURIComponent(noteId)}/backlinks`,
            createConfig(accessToken)
        );
        return response.data.data;
    },

    /**
     * Sauvegarde une note (création ou mise à jour)
     * @param note - Note à sauvegarder
     * @param accessToken - Token d'authentification  
     * @param historyOptions - Options pour l'historique (authorName, collaborators)
     * @returns Note sauvegardée avec ID
     */
    save: async (
        note: Partial<Note>,
        accessToken: string,
        historyOptions?: {
            authorName?: string;
            collaborators?: Array<{ userId: string; displayName: string; joinedAt?: Date }>;
        },
        options?: UpdateContentOptions
    ): Promise<Note> => {
        try {
            // Préparer le payload avec les options d'historique et, pour une mise à
            // jour, la garde de version facultative (C22 SP1, § 2.6).
            const payload: Record<string, unknown> = {
                ...note,
                historyOptions: historyOptions || {}
            };
            if (note._id && options?.ifUnmodifiedSince) payload.ifUnmodifiedSince = options.ifUnmodifiedSince;
            
            if (!note._id) {
                // Nouvelle note
                console.log('Note save async - création');
                const response = await axios.post('/api/notes', payload, createConfig(accessToken));
                // L'API retourne { success: true, data: savedNote }
                return response.data.data || response.data;
            } else {
                // Mise à jour note existante
                console.log('Note update async - modification', historyOptions?.collaborators?.length || 0, 'collaborateurs');
                const response = await axios.put(`/api/notes/${note._id}`, payload, createConfig(accessToken));
                // L'API retourne { success: true, data: updatedNote }
                return response.data.data || response.data;
            }
        } catch (error) {
            console.error('Erreur lors de la sauvegarde de la note:', error);
            // Garde de version : la note a changé depuis son chargement (409).
            if (axios.isAxiosError(error) && error.response?.status === 409) {
                const refus = error.response.data as { message?: string } | undefined;
                throw new NoteModifieeAilleursError(refus?.message);
            }
            // Un refus motivé du serveur (403 codé : la citation est réservée à son
            // propriétaire, C23 bis ; 400 codé : note invalide pour le schéma,
            // 28/10/2026) se lit tel quel dans la barre d'état.
            if (axios.isAxiosError(error) && (error.response?.status === 403 || error.response?.status === 400)) {
                const refus = error.response.data as { code?: string; message?: string } | undefined;
                if (refus?.code && refus.message) throw new Error(refus.message);
            }
            throw new Error('Impossible de sauvegarder la note');
        }
    },

    /**
     * Supprime une note
     * @param note - Note à supprimer
     * @param accessToken - Token d'authentification
     * @returns Note supprimée pour confirmation
     */
    delete: async (note: Note, accessToken: string): Promise<Note> => {
        try {
            console.log('Note delete async');
            await axios.delete(`/api/notes/${note._id}`, createConfig(accessToken));
            return note;
        } catch (error) {
            console.error('Erreur lors de la suppression de la note:', error);
            throw new Error('Impossible de supprimer la note');
        }
    },

    /**
     * Récupère une note par ID avec le niveau de permission
     * @param noteId - ID de la note
     * @param accessToken - Token d'authentification
     * @returns Note et permission ('owner' | 'edit' | 'read'), ou null si non trouvée
     */
    /**
     * Comme `getById`, mais l'erreur axios remonte telle quelle (C28 SP2) : l'appelant distingue la
     * note supprimée (404) d'une panne (réseau, 500, jeton) au lieu de les confondre dans un `null`.
     */
    getByIdStrict: async (noteId: string, accessToken: string): Promise<{ note: Note; permission: 'owner' | 'edit' | 'read' }> => {
        const data = (await axios.get(`/api/notes/${encodeURIComponent(noteId)}`, createConfig(accessToken))).data;
        if (!data?.success || !data.data) throw Object.assign(new Error('Note introuvable'), { response: { status: 404 } });
        return { note: data.data, permission: data.permission || 'owner' };
    },

    getById: async (noteId: string, accessToken: string): Promise<{ note: Note; permission: 'owner' | 'edit' | 'read' } | null> => {
        try {
            const response = await axios.get(`/api/notes/${noteId}`, createConfig(accessToken));
            const data = response.data;
            if (data.success && data.data) {
                return {
                    note: data.data,
                    permission: data.permission || 'owner'
                };
            }
            return null;
        } catch (error) {
            console.error('Erreur lors de la récupération de la note:', error);
            return null;
        }
    },

    /**
     * Recherche de notes par contenu
     * @param query - Terme de recherche
     * @param accessToken - Token d'authentification
     * @returns Notes correspondant à la recherche
     */
    search: async (query: string, accessToken: string): Promise<Note[]> => {
        try {
            const response = await axios.get<Note[]>(
                `/api/notes/search?q=${encodeURIComponent(query)}`, 
                createConfig(accessToken)
            );
            return response.data;
        } catch (error) {
            console.error('Erreur lors de la recherche de notes:', error);
            throw new Error('Impossible d\'effectuer la recherche');
        }
    }
};

export default noteService;
